//------------------------------------------------------------------------
// Doughnut Charts
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
import {Chart, ArcElement, DoughnutController, Tooltip} from 'chart.js';
import { cursorPositioner } from '../helpers/chart-js-helpers';
import debounce from "lodash/debounce";

Chart.register(ArcElement, DoughnutController, Tooltip);
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';

/**
 * Custom positioner - Allows for the tooltip to follow the cursor's pointer
 */
Tooltip.positioners.cursor = cursorPositioner;

class DoughnutChart {
  constructor(config) {
    this.el = config.el;
    this.segments = JSON.parse(this.el.getAttribute('data-segments'));
    this.canvas = this.el.querySelector('canvas');
    this.legendItems = this.el.querySelectorAll('[data-legend-item]');
    this.statisticFeature = JSON.parse(this.el.getAttribute('data-statistic-feature'));

    if (!this.segments) {
      console.error(`Unable to find [data-segments] data on #${this.el.id}.`);
      return false;
    }

    if (!this.canvas) {
      console.error(`Unable to initialize doughnut chart, no matching element for ${this.el}.`);
      return false;
    }

    this.setData();
    this.init();
  }

  init() {
    // plugin that displays the featured in the center of the donut chart
    let mql = window.matchMedia("(min-width: 700px)");

    mql.addEventListener('change', () => {
      // Returning the same values that onResize would for chart.js
      this.resizeHandler(this.chart, {width: window.innerWidth, height: window.innerHeight});
    });

    const featuredTitle = {
      id: 'featuredTitle',
      beforeDraw: (chart, args, options) => {
        /* eslint no-unused-vars: "off" */
        const { ctx, chartArea: { top, right, bottom, left, width, height } } = chart;

        ctx.save();

        // don't draw if the content author is positioning the statistic feature to the right or disabling it
        if (!options.show || (options.position !== 'center')) {
          return null;
        }

        options.textLines.forEach((textLine, index) => {
          ctx.fillStyle = textLine.fontColor;
          ctx.font = `${textLine.fontWeight} ${textLine.fontSize}px ${textLine.fontFamily}`;
          ctx.textAlign = textLine.textAlign

          let lineHeight = 30;
          // We are multiplying height by .36 because that is distance the text is supposed to be from the chart
          // We are multiplying line height by index to get the gutter space so the text don't sit on each other
          // The font sizes are different so we will need to decrease the line height;
          let yPos = (height * .45 );
          let xPos = width / 2;

          if (index !== 0) {
            yPos +=  (lineHeight * index);
          } else {
            yPos -= lineHeight;
          }

          ctx.fillText(textLine.text, xPos, yPos);
        });
      }
    };

    let superheaderFontSize = (window.innerWidth > 700) ? 16 : 14;
    let headerFontSize = (window.innerWidth > 700) ? 54 : 33;
    let subheaderFontSize = (window.innerWidth > 700) ? 22 : 18;

    this.chart = new Chart(this.canvas, {
      type: 'doughnut',
      data: {
        labels: this.dataLabels,
        datasets: [{
          label: this.title,
          data: this.dataValues,
          backgroundColor: this.colors,
          borderWidth: 2,
          borderColor: '#fff',
          hoverBackgroundColor: this.colors,
          hoverBorderColor: '#fff',
        }]
      },
      options: {
        datasets: {
          doughnut: {
            cutout: '70%',
          }
        },
        plugins: {
          tooltip: {
            enabled: false,
            position: 'cursor',
            // We are binding the method to have access to this.toolTipEl
            external: mql.matches ? this.externalTooltipHandler.bind(this) : false,
          },
          featuredTitle: {
            show: true,
            position: this.statisticFeature.position,
            textLines: [
              {
                text: this.statisticFeature.superheader ? this.statisticFeature.superheader.toUpperCase() : '',
                fontSize: superheaderFontSize,
                fontFamily: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 'bold',
                fontColor: '#480751', // fs-color("text", "purple")
                textAlign: 'center'
              },
              {
                text: this.statisticFeature.header,
                fontSize: headerFontSize,
                fontFamily: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 'bold',
                fontColor: '#480751', // fs-color("text", "purple")
                textAlign: 'center'
              },
              {
                text: this.statisticFeature.label,
                fontSize: subheaderFontSize,
                fontFamily: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 'bold',
                fontColor: '#480751', // fs-color("text", "purple")
                textAlign: 'center'
              }
            ],
          }
        },
      },
      plugins: [featuredTitle]
    });

    this.el.addEventListener('mouseout-chart', debounce(this.mouseoutChartHandler.bind(this), 10, { leading: true, trailing: false}));
    this.el.addEventListener('mouseover-chart', debounce(this.mouseoverChartHandler.bind(this), 10, { leading: true, trailing: false}));

    this.legendItems.forEach(item => {
      item.addEventListener('mouseenter', evt => {
        let selectedIndex = parseInt(evt.target.getAttribute('data-index'), 10);

        // deactivate all other items
        this.legendItems.forEach(legendItem => {
          if (legendItem === evt.target) {
            legendItem.classList.remove('is-inactive');
          } else {
            legendItem.classList.add('is-inactive');
          }
        });

        // Update Chart colors on hover
        this.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
          let colorLength = color.length;
          // Remove the original opacity from the color
          let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

          if (selectedIndex === index) {
            colors[index] = baseColor;
          } else {
            colors[index] = baseColor + '99';
          }
        });

        this.chart.update();

      });

      item.addEventListener('mouseleave', () => {
        this.legendItems.forEach(legendItem => {
          legendItem.classList.remove('is-inactive');
        });

        this.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
          let colorLength = color.length;
          // Remove the original opacity from the color
          let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

          colors[index] = baseColor;
        });

        this.chart.update();
      });
    });
  }

  destroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  resizeHandler(chart, size) {
    let { width } = size;
    let tooltip = (width > 700) ? this.externalTooltipHandler.bind(this) : false;

    chart.options.plugins.tooltip.external = tooltip;
    chart.update();
  }

  mouseoutChartHandler(evt) {
    this.legendItems.forEach(item => item.classList.remove('is-inactive'));

    // Revert chart background color to its original form
    evt.detail.tooltip.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
      let colorLength = color.length;
      // Remove the original opacity from the color
      let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

      colors[index] = baseColor;
    });

    evt.detail.tooltip.chart.update();
  }

  mouseoverChartHandler(evt) {
    const { tooltip } = evt.detail;
    const selectedIndex = tooltip.dataPoints[0].dataIndex;
    // const datasetIndex = 0; // this is always 0 because we only have one dataset

    this.legendItems.forEach(item => {
      let legendIndex = item.getAttribute('data-index');

      if (parseInt(legendIndex, 10) === parseInt(selectedIndex, 10)) {
        item.classList.remove('is-inactive');
      } else {
        item.classList.add('is-inactive');
      }
    });


    // Update Chart colors on hover
    evt.detail.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
      let colorLength = color.length;
      // Remove the original opacity from the color
      let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

      if (selectedIndex === index) {
        colors[index] = baseColor;
      } else {
        colors[index] = baseColor + '99';
      }
    });

    evt.detail.chart.update();
  }

  externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.nextElementSibling;

    // Custom event detail that is being passed through
    let detail = {
      detail: {
        el: this.el,
        tooltip,
        chart
      }
    };

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      const mouseoutChart = new CustomEvent("mouseout-chart", detail);

      this.el.dispatchEvent(mouseoutChart);
      return;
    }

    // Set Text
    if (tooltip.body) {
      let dataPoints = tooltip.dataPoints || [];
      const tooltipFragment = document.createDocumentFragment();

      dataPoints.forEach(item => {
        let titleEl = document.createElement('span');
        let titleText = document.createTextNode(item.label);
        let valueEl = document.createElement('span');
        let valueText = document.createTextNode(item.parsed + '%');

        titleEl.classList.add('ChartTooltip-title');
        valueEl.classList.add('ChartTooltip-value');

        titleEl.appendChild(titleText);
        valueEl.appendChild(valueText);

        tooltipFragment.appendChild(titleEl);
        tooltipFragment.appendChild(valueEl);
      });

      while(tooltipEl.firstChild) {
        tooltipEl.firstChild.remove();
      }

      tooltipEl.appendChild(tooltipFragment);
    }

    const { offsetLeft, offsetTop } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = offsetLeft + (tooltip.caretX + 10) + 'px';
    tooltipEl.style.top = offsetTop + (tooltip.caretY + 10) + 'px';

    // Emit event when we are hovering over the segment
    const mouseoverChart = new CustomEvent("mouseover-chart", detail);
    this.el.dispatchEvent(mouseoverChart);
  }

  /**
   * Retrieves data-* attributes from the Chart's container and create instance variables
   * of each in the correct format for Chart.js
   *
   * These are the data-* attributes that are being used:
   * - data-segments = the segments from the block's segments repeater field
   * - data-title = the chart's title
   * - data-type = the type of circle chart that is being used
   *
   * isntance variables that are being created:
   * - this.dataValues
   * - this.colors
   * - this.title
   * - this.type
   * @return null
   */
    setData() {
      let data = this.segments;
      let title = this.el.getAttribute('data-title');

      this.dataLabels = data.map(segment => {
        return segment.name;
      });

      this.dataValues = data.map(segment => segment.value);

      this.colors = data.map(segment => segment.color);

      this.title = title;
    }
}

const init = () => {
  let doughnutCharts = document.querySelectorAll('[data-type="doughnut"]');

  if (doughnutCharts) {
    doughnutCharts.forEach(el => {
      new DoughnutChart({ el });
    });
  }
};

// Init on initial page load
init();
